import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useState } from 'react';

import { CarrierNameSelect } from '@/components/carrier-directory/CarrierNameSelect';
import { CarrierPocketbook } from '@/components/carrier-directory/CarrierPocketbook';
import { DirectoryHandlesPreview } from '@/components/carrier-directory/DirectoryHandlesPreview';

import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { IntakeFormData } from '../form/types';
import RemoveButton from './RemoveButton';
import StateSelect from './StateSelect';

export interface DefendantCardProps {
  index: number;
  defendant: IntakeFormData['insurance']['defendants'][number];
  onFieldChange: (field: string, value: any) => void;
  onRemove: () => void;
}

const DefendantCard: React.FC<DefendantCardProps> = ({
  index,
  defendant,
  onFieldChange,
  onRemove,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const path = (field: string) => `defendants[${index}].${field}`;

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Local preview state for directory selection to ensure immediate UI feedback
  const [dirCarrierId, setDirCarrierId] = useState<string | null>(null);
  const [dirDivisionId, setDirDivisionId] = useState<string | null>(null);

  // Keep local preview state in sync with form values (supports legacy camelCase keys)
  React.useEffect(() => {
    const anyDef = defendant as unknown as {
      carrier_v2_id?: string | null;
      carrierV2Id?: string | null;
      carrier_division_id?: string | null;
      carrierDivisionId?: string | null;
    };
    const cId = anyDef.carrier_v2_id ?? anyDef.carrierV2Id;
    const dId = anyDef.carrier_division_id ?? anyDef.carrierDivisionId;
    // Only update local state when we have defined values to avoid flicker
    if (typeof cId !== 'undefined' && cId !== null) {
      setDirCarrierId(cId);
    }
    if (typeof dId !== 'undefined') {
      setDirDivisionId(dId);
    }
  }, [defendant]);

  // Show key information in collapsed state
  const getSummaryText = () => {
    const name = defendant.name || 'Unnamed defendant';
    const vehicle = defendant.makeModelYear || defendant.driversLicensePlate;
    const insurance = defendant.insuranceCompanyName;

    const parts = [name];
    if (vehicle) parts.push(vehicle);
    if (insurance) parts.push(insurance);

    return parts.join(' • ');
  };

  return (
    <div className="relative border border-gray-200 rounded-md overflow-hidden">
      {/* Header - Always visible */}
      <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
        <div
          role="button"
          tabIndex={0}
          aria-label={
            isExpanded
              ? 'Collapse defendant details'
              : 'Expand defendant details'
          }
          onClick={handleToggleExpand}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleToggleExpand();
          }}
          className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-200 hover:bg-gray-100 transition-colors"
        >
          <span className="flex-shrink-0 p-1 text-gray-500 group-hover:text-gray-700 transition-colors">
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900">
                Defendant #{index + 1}
              </span>
            </div>
            <div className="text-sm text-gray-600 truncate">
              {getSummaryText()}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 ml-2">
          <RemoveButton onClick={onRemove} className="relative top-0 right-0" />
        </div>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Defendant Info Subsection */}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Defendant Info
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-name-${index}`}
                >
                  Name
                </Label>
                <Input
                  id={`defendant-name-${index}`}
                  value={defendant.name}
                  onChange={(e) => onFieldChange(path('name'), e.target.value)}
                  placeholder="Full name"
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-address-${index}`}
                >
                  Address
                </Label>
                <Input
                  id={`defendant-address-${index}`}
                  value={defendant.address}
                  onChange={(e) =>
                    onFieldChange(path('address'), e.target.value)
                  }
                  placeholder="Address"
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          {/* License Info Subsection */}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              License Info
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-dlnum-${index}`}
                >
                  Driver&apos;s License Number
                </Label>
                <Input
                  id={`defendant-dlnum-${index}`}
                  value={defendant.driversLicenseNumber}
                  onChange={(e) =>
                    onFieldChange(path('driversLicenseNumber'), e.target.value)
                  }
                  placeholder="Driver's License Number"
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-dlstate-${index}`}
                >
                  Driver&apos;s License State
                </Label>
                <StateSelect
                  id={`defendant-dlstate-${index}`}
                  value={defendant.driversLicenseState}
                  onChange={(v) =>
                    onFieldChange(path('driversLicenseState'), v)
                  }
                />
              </div>
            </div>
          </div>

          {/* Vehicle Info Subsection */}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Vehicle Info
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-make-model-${index}`}
                >
                  Make / Model / Year
                </Label>
                <Input
                  id={`defendant-make-model-${index}`}
                  value={defendant.makeModelYear}
                  onChange={(e) =>
                    onFieldChange(path('makeModelYear'), e.target.value)
                  }
                  placeholder="e.g., 2018 Toyota Camry"
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-plate-${index}`}
                >
                  License Plate
                </Label>
                <Input
                  id={`defendant-plate-${index}`}
                  value={defendant.driversLicensePlate}
                  onChange={(e) =>
                    onFieldChange(path('driversLicensePlate'), e.target.value)
                  }
                  placeholder="Plate"
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          {/* Insurance Info Subsection */}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Insurance Info
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  Insurance Company Name
                </Label>
                <div className="flex items-center gap-2">
                  <CarrierNameSelect
                    value={defendant.insuranceCompanyName}
                    onChange={(name) =>
                      onFieldChange(path('insuranceCompanyName'), name)
                    }
                    onSelectDirectory={(carrierId, divisionId) => {
                      // Update local preview immediately
                      setDirCarrierId(carrierId);
                      setDirDivisionId(divisionId || null);
                      // Persist snake_case (new) keys
                      onFieldChange(path('carrier_v2_id'), carrierId);
                      onFieldChange(
                        path('carrier_division_id'),
                        divisionId || null,
                      );
                      // Backward-compat: also write legacy camelCase keys
                      onFieldChange(path('carrierV2Id'), carrierId);
                      onFieldChange(
                        path('carrierDivisionId'),
                        divisionId || null,
                      );
                    }}
                  />
                  <CarrierPocketbook
                    trigger={
                      <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors border border-blue-200 hover:border-blue-300"
                        title="Add Carrier Contact"
                      >
                        <span className="text-sm font-medium">Add</span>
                      </button>
                    }
                    actions={['use', 'copy']}
                    defaultSelectedCarrierId={dirCarrierId || undefined}
                    onSelectDivision={(carrier, divisionId) => {
                      setDirCarrierId(carrier.id);
                      setDirDivisionId(divisionId || null);
                      onFieldChange(path('carrier_v2_id'), carrier.id);
                      onFieldChange(
                        path('carrier_division_id'),
                        divisionId || null,
                      );
                      onFieldChange(path('carrierV2Id'), carrier.id);
                      onFieldChange(
                        path('carrierDivisionId'),
                        divisionId || null,
                      );
                    }}
                    onAttachSelected={(_, selected) => {
                      const toValue = (h: any): string => {
                        if (h.value) return h.value;
                        const parts = [
                          h.addressLine1,
                          h.city,
                          h.state,
                          h.zip,
                          h.country,
                        ].filter(Boolean);
                        return parts.join(', ');
                      };
                      const mapped =
                        selected?.map((h: any) => ({
                          handle_type: h.handleType,
                          value: toValue(h),
                          name: h.name,
                        })) || [];
                      const existing =
                        (defendant as unknown as { carrier_handles?: any[] })
                          .carrier_handles || [];
                      onFieldChange(
                        path('carrier_handles'),
                        [...existing, ...mapped].filter(Boolean),
                      );
                    }}
                  />
                </div>
                {/* Show handles when either new or legacy ID is present */}
                {dirCarrierId ||
                (defendant as unknown as { carrier_v2_id?: string | null })
                  .carrier_v2_id ||
                (defendant as unknown as { carrierV2Id?: string | null })
                  .carrierV2Id ? (
                  <div className="mt-2">
                    <DirectoryHandlesPreview
                      carrierId={
                        dirCarrierId ||
                        (
                          defendant as unknown as {
                            carrier_v2_id?: string | null;
                          }
                        ).carrier_v2_id ||
                        (
                          defendant as unknown as {
                            carrierV2Id?: string | null;
                          }
                        ).carrierV2Id ||
                        ''
                      }
                      divisionId={
                        dirDivisionId ??
                        (
                          defendant as unknown as {
                            carrier_division_id?: string | null;
                          }
                        ).carrier_division_id ??
                        (
                          defendant as unknown as {
                            carrierDivisionId?: string | null;
                          }
                        ).carrierDivisionId ??
                        null
                      }
                      title="Carrier Contacts (Directory)"
                      className="rounded border border-gray-200 p-2"
                    />
                  </div>
                ) : null}
              </div>
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-policy-holder-${index}`}
                >
                  Policy Holder Name
                </Label>
                <Input
                  id={`defendant-policy-holder-${index}`}
                  value={defendant.policyHolderName}
                  onChange={(e) =>
                    onFieldChange(path('policyHolderName'), e.target.value)
                  }
                  placeholder="Name on the other party's policy"
                  aria-label="Defendant policy holder name"
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-policy-number-${index}`}
                >
                  Policy Number
                </Label>
                <Input
                  id={`defendant-policy-number-${index}`}
                  value={defendant.policyNumber}
                  onChange={(e) =>
                    onFieldChange(path('policyNumber'), e.target.value)
                  }
                  placeholder="Other party's policy number"
                  aria-label="Defendant policy number"
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label
                  className="text-sm font-medium"
                  htmlFor={`defendant-claim-number-${index}`}
                >
                  Claim Number
                </Label>
                <Input
                  id={`defendant-claim-number-${index}`}
                  value={defendant.claimNumber}
                  onChange={(e) =>
                    onFieldChange(path('claimNumber'), e.target.value)
                  }
                  placeholder="Claim number if filed"
                  aria-label="Defendant claim number"
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefendantCard;
